import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import OffersCallToAction from './OffersCallToAction';
import { useCanvasSession } from '../src/useCanvasSession';
import { Box } from 'vendor/material';
import { Loader } from '@tackle-io/platform-ui';
import ListOfOffersForOpportunity from './ListOfOffersForOpportunity';
import ApiContext from 'pages/PrivateOffers/pages/Next/generic/ApiContext/apiContext';
import { AwsPrivateOffer } from 'pages/PrivateOffers/pages/Next/aws/api/types/AwsPrivateOffer';
import { useRbac } from 'utils/rbac';
import NotEnoughPermissionsTabContent from '../src/components/NotEnoughPermissionsTabContent';
import { ApiContextProvider } from 'pages/PrivateOffers/pages/Next/generic/ApiContext/ApiContextProvider';
import { Marketplace } from 'pages/PrivateOffers/pages/Next/generic/types/TackleOffer';
import { OffersCommonOfferResponse } from 'pages/PrivateOffers/pages/Next/generic/api/types/OffersCommonOfferResponse';

export const OffersTabContentContext = createContext({
  fetchOffersForOpportunity: () => {},
});

const OffersTabContent = () => {
  const {
    awsApi: { getOffersForOpportunity },
    marketplaceAgnosticApi: { getAllOffersForOpportunity },
  } = useContext(ApiContext);

  const session = useCanvasSession();
  const [offers, setOffers] = useState<AwsPrivateOffer[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [msftOffers, setMsftOffers] = useState<OffersCommonOfferResponse[]>([]);

  /**
   * This is a temporary workaround to get the MSFT offers for the opportunity,
   * until we have a path forward to include AWS and keeo all the existing functinoality.
   */
  const getMsftOffersByOppId = useCallback(
    async (oppId: string) => {
      const offers = await getAllOffersForOpportunity(oppId);
      return offers.filter(
        (offer) => offer.cloud === Marketplace.Azure || offer.cloud === 'msft',
      );
    },
    [getAllOffersForOpportunity],
  );

  const fetchOffersForOpportunity = useCallback(async () => {
    if (!session.record?.Id) return;
    setLoading(true);
    try {
      const offersForOpportunity = await getOffersForOpportunity(
        session.record.Id,
      );
      const msftOffers = await getMsftOffersByOppId(session.record.Id);
      setOffers(offersForOpportunity);
      setMsftOffers(msftOffers);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [getMsftOffersByOppId, getOffersForOpportunity, session.record.Id]);

  useEffect(() => {
    fetchOffersForOpportunity();
  }, [fetchOffersForOpportunity]);

  return (
    <OffersTabContentContext.Provider
      value={{
        fetchOffersForOpportunity,
      }}
    >
      <Box overflow="auto" height="100%" position="relative">
        {loading && <Loader />}
        {error && <div>Error: {error.message}</div>}
        {!loading && (!offers || offers?.length === 0) && (
          <OffersCallToAction />
        )}
        {offers?.length > 0 && (
          <ListOfOffersForOpportunity offers={offers} msftOffers={msftOffers} />
        )}
      </Box>
    </OffersTabContentContext.Provider>
  );
};

const OffersTabContentWithProvider = () => {
  const { hasPermission, loading } = useRbac();
  if (loading) {
    return <Loader />;
  }

  if (!hasPermission('offers:ListOffers')) {
    return (
      <NotEnoughPermissionsTabContent blurb="Your permissions are not configured to view or create offers. Contact your Salesforce admin to request changes." />
    );
  }
  return (
    <ApiContextProvider>
      <OffersTabContent />
    </ApiContextProvider>
  );
};

export default OffersTabContentWithProvider;
